/**
 * Keybinding utility functions
 * 
 * Handles key event processing, string building, and event filtering
 */

/**
 * Build a standardized key string from a keyboard event
 * @param {KeyboardEvent} event - The keyboard event
 * @returns {string} - Standardized key representation
 */
export const buildKeyString = (event) => {
  const parts = [];
  
  // Add modifiers in consistent order
  if (event.ctrlKey || event.metaKey) {
    // Treat Cmd (Meta) and Ctrl as equivalent for cross-platform consistency
    parts.push(event.metaKey ? 'Meta' : 'Control');
  }
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');
  
  // Handle special keys
  const specialKeys = {
    ' ': 'Space',
    'Enter': 'Enter',
    'Escape': 'Escape',
    'Tab': 'Tab',
    'Backspace': 'Backspace',
    'Delete': 'Delete',
    'ArrowUp': 'Up',
    'ArrowDown': 'Down',
    'ArrowLeft': 'Left',
    'ArrowRight': 'Right',
    'Home': 'Home',
    'End': 'End',
    'PageUp': 'PageUp',
    'PageDown': 'PageDown',
    'Insert': 'Insert',
  };
  
  // Get the key
  let key = event.key;
  
  // Handle special cases
  if (specialKeys[key]) {
    key = specialKeys[key];
  } else if (key.length === 1) {
    // Single character - use lowercase unless Shift is held for non-letter
    if (event.shiftKey && /[a-z]/.test(key)) {
      key = key.toUpperCase();
    } else if (!event.shiftKey && /[A-Z]/.test(key)) {
      key = key.toLowerCase();
    }
  }
  
  // Add the key itself
  parts.push(key);
  
  return parts.join('+');
};

/**
 * Determine if a key event should be ignored by the keybinding system
 * @param {KeyboardEvent} event - The keyboard event
 * @returns {boolean} - True if the event should be ignored
 */
export const shouldIgnoreKeyEvent = (event) => {
  const target = event.target;
  const tagName = (target?.tagName || '').toLowerCase();
  
  // Always ignore if in input/textarea unless specifically bound
  if (tagName === 'input' || tagName === 'textarea') {
    // Allow Escape to work in inputs for clearing sequences
    if (event.key === 'Escape') return false;
    
    // Allow specific shortcuts in inputs (Cmd+A, Cmd+C, etc.)
    if ((event.metaKey || event.ctrlKey) && !event.altKey) {
      const allowedInInputs = ['a', 'c', 'v', 'x', 'z', 'y'];
      if (allowedInInputs.includes(event.key.toLowerCase())) {
        return true; // Let browser handle these
      }
    }
    
    return true; // Ignore most keys in inputs
  }
  
  // Ignore if contenteditable
  if (target && target.contentEditable === 'true') {
    return event.key !== 'Escape';
  }
  
  // Ignore if target has data-ignore-keybindings attribute
  if (target && target.hasAttribute?.('data-ignore-keybindings')) {
    return true;
  }
  
  // Ignore browser shortcuts we shouldn't override
  if (event.metaKey || event.ctrlKey) {
    const browserShortcuts = [
      'r', 'w', 't', 'n', 'l', 'f', 'd', 'u', 'j', 'o', 'p',
      'F5', 'F12', 'Tab'
    ];
    
    if (browserShortcuts.includes(event.key)) {
      return true; // Let browser handle these
    }
  }
  
  return false;
};

/**
 * Format a key string for display to users
 * @param {string} keyString - Key string from buildKeyString
 * @returns {string} - Human-readable format
 */
export const formatKeyDisplay = (keyString) => {
  const isMac = navigator.platform.toLowerCase().includes('mac');
  
  return keyString
    .replace(/Meta\+/g, isMac ? '⌘' : 'Cmd+')
    .replace(/Control\+/g, isMac ? '⌃' : 'Ctrl+')
    .replace(/Alt\+/g, isMac ? '⌥' : 'Alt+')
    .replace(/Shift\+/g, isMac ? '⇧' : 'Shift+')
    .replace(/Space/g, '␣')
    .replace(/Enter/g, '↵')
    .replace(/Escape/g, '⎋')
    .replace(/Tab/g, '⇥')
    .replace(/Up/g, '↑')
    .replace(/Down/g, '↓')
    .replace(/Left/g, '←')
    .replace(/Right/g, '→');
};

/**
 * Parse a user-friendly key string into our standard format
 * @param {string} userString - User input like "cmd+k" or "ctrl+shift+p"
 * @returns {string} - Standardized key string
 */
export const parseUserKeyString = (userString) => {
  if (!userString) return '';
  
  const normalized = userString.toLowerCase().trim();
  const parts = normalized.split('+').map(p => p.trim());
  
  const result = [];
  let hasModifier = false;
  
  for (const part of parts) {
    switch (part) {
      case 'cmd':
      case 'meta':
      case 'command':
        result.push('Meta');
        hasModifier = true;
        break;
      case 'ctrl':
      case 'control':
        result.push('Control');
        hasModifier = true;
        break;
      case 'alt':
      case 'option':
        result.push('Alt');
        hasModifier = true;
        break;
      case 'shift':
        result.push('Shift');
        hasModifier = true;
        break;
      case 'space':
        result.push('Space');
        break;
      case 'enter':
      case 'return':
        result.push('Enter');
        break;
      case 'esc':
      case 'escape':
        result.push('Escape');
        break;
      case 'tab':
        result.push('Tab');
        break;
      default:
        // Regular key
        if (part.length === 1) {
          result.push(hasModifier ? part.toLowerCase() : part);
        } else {
          result.push(part);
        }
    }
  }
  
  return result.join('+');
};

/**
 * Check if two key strings are equivalent
 * @param {string} a - First key string
 * @param {string} b - Second key string
 * @returns {boolean} - True if equivalent
 */
export const areKeysEquivalent = (a, b) => {
  if (!a || !b) return false;
  
  // Normalize both strings
  const normalizeKey = (keyStr) => {
    return keyStr
      .toLowerCase()
      .replace(/meta/g, 'meta') // Keep meta as-is for now
      .replace(/control/g, 'control')
      .split('+')
      .sort()
      .join('+');
  };
  
  return normalizeKey(a) === normalizeKey(b);
};

/**
 * Debounce function for key events
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} - Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Validate a key sequence format
 * @param {string} sequence - Key sequence to validate
 * @returns {Object} - { valid: boolean, error?: string }
 */
export const validateKeySequence = (sequence) => {
  if (!sequence || typeof sequence !== 'string') {
    return { valid: false, error: 'Sequence must be a non-empty string' };
  }
  
  const trimmed = sequence.trim();
  if (!trimmed) {
    return { valid: false, error: 'Sequence cannot be empty' };
  }
  
  // Check for valid characters
  const validPattern = /^[a-zA-Z0-9\s+\-[\](){}\\/.,:;'"` ~!@#$%^&*_=|<>?]+$/;
  if (!validPattern.test(trimmed)) {
    return { valid: false, error: 'Sequence contains invalid characters' };
  }
  
  // Check individual keys
  const keys = trimmed.split(' ');
  for (const key of keys) {
    if (!key.trim()) {
      return { valid: false, error: 'Empty key in sequence' };
    }
  }
  
  return { valid: true };
};

/**
 * Get platform-specific modifier names
 * @returns {Object} - Platform-specific modifier mappings
 */
export const getPlatformModifiers = () => {
  const isMac = navigator.platform.toLowerCase().includes('mac');
  
  return {
    primary: isMac ? 'Meta' : 'Control',
    primarySymbol: isMac ? '⌘' : '⌃',
    secondary: 'Alt',
    secondarySymbol: isMac ? '⌥' : 'Alt',
    tertiary: 'Shift',
    tertiarySymbol: '⇧',
  };
};

/**
 * Create a cross-platform key binding
 * @param {string} key - The key (without modifiers)
 * @param {Object} options - Modifier options
 * @returns {string} - Platform-appropriate key string
 */
export const createCrossPlatformBinding = (key, options = {}) => {
  const { primary } = getPlatformModifiers();
  const parts = [];
  
  if (options.primary) parts.push(primary);
  if (options.secondary) parts.push('Alt');
  if (options.tertiary) parts.push('Shift');
  
  parts.push(key);
  
  return parts.join('+');
};